import React, {useState, useEffect, useRef, useMemo} from 'react';
import {GenericDatePickerProps} from '../../interfaces/components/datePicker';
import {Platform, Keyboard, TouchableOpacity, View} from 'react-native';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import styled from 'styled-components/native';
import TextInput from '../textInput';

dayjs.extend(customParseFormat);

let CustomTextInput: any;
try {
  CustomTextInput = require('../textInput').default;
} catch (e) {}

const StyledTextInput = styled.TextInput`
  border-width: 1px;
  border-color: #ccc;
  border-radius: 4px;
  padding: 8px;
  min-width: 150px;
`;

const GenericDatePicker = ({
  label,
  value = '',
  onValueChange,
  style,
  format,
  maxDate = '',
  minDate,
  disabled,
  isRequired,
  customComponent,
  mode = 'date',
  date: propDate,
  onDateChange,
  ...props
}: GenericDatePickerProps) => {
  const inputWebRef = useRef<any>(null);

  const defaultFormat = useMemo(() => {
    switch (mode) {
      case 'time':
        return 'HH:mm';
      case 'datetime':
        return 'YYYY-MM-DDTHH:mm'; // HTML `datetime-local` format
      default:
        return 'YYYY-MM-DD';
    }
  }, [mode]);

  const effectiveFormat = format || defaultFormat;

  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (propDate) {
      return propDate;
    }
    if (!value) {
      return null;
    }
    const parsed = dayjs(value, effectiveFormat, true);
    return parsed.isValid() ? parsed.toDate() : null;
  });

  const [show, setShow] = useState<boolean>(false);

  useEffect(() => {
    if (propDate) {
      setSelectedDate(propDate);
      return;
    }

    if (!value) {
      setSelectedDate(null);
      return;
    }
    const parsed = dayjs(value, effectiveFormat, true);
    setSelectedDate(parsed.isValid() ? parsed.toDate() : null);
  }, [value, effectiveFormat, propDate]);

  const onChange = (date: Date): void => {
    setSelectedDate(date);

    if (onDateChange) {
      onDateChange(date);
    }

    const formattedDate = dayjs(date).format(effectiveFormat);
    if (onValueChange) {
      onValueChange(formattedDate);
    }

    setShow(false);
  };

  const getValue = (): string => {
    if (!selectedDate) {
      return '';
    }
    return dayjs(selectedDate).format(effectiveFormat);
  };

  const showDatePicker = () => {
    Keyboard.dismiss();
    setShow(true);
  };

  const handleWebChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const dateStr = input.value;

    const parsed = dayjs(dateStr, effectiveFormat, true);
    if (parsed.isValid()) {
      onChange(parsed.toDate());
    }
  };

  // Only try to import DatePicker on native platforms to avoid webpack errors
  let NativeDatePicker: any = null;
  if (Platform.OS !== 'web') {
    try {
      // Use dynamic require within try/catch to prevent build errors
      NativeDatePicker = require('react-native-date-picker').default;
    } catch (e) {
      // Just leave NativeDatePicker as null if not available
    }
  }

  const openWebPicker = () => {
    if (inputWebRef.current?.showPicker) {
      inputWebRef.current.showPicker(); // works in Chromium-based browsers
    } else {
      inputWebRef.current?.focus(); // fallback
    }
  };

  // On web, render a simple date input
  if (Platform.OS === 'web') {
    // Map mode to HTML input types
    const getInputType = () => {
      switch (mode) {
        case 'time':
          return 'time';
        case 'datetime':
          return 'datetime-local';
        default:
          return 'date';
      }
    };

    // Get correct format for input value based on mode
    const getInputValueFormat = () => {
      switch (mode) {
        case 'time':
          return 'HH:mm';
        case 'datetime':
          return 'YYYY-MM-DDTHH:mm';
        default:
          return 'YYYY-MM-DD';
      }
    };

    return (
      <TouchableOpacity
        style={[
          style,
          disabled && {
            opacity: 0.5,
          },
        ]}
        activeOpacity={0.7}
        disabled={disabled}
        onPress={openWebPicker}>
        {customComponent ? (
          customComponent()
        ) : (
          <View style={{width: '100%', pointerEvents: 'none'}}>
            <TextInput
              label={label}
              value={getValue()}
              editable={false}
              onChangeText={onValueChange}
              showRightIcon
              rightIconName={'calendar'}
              isRequired={isRequired}
              {...props}
            />
          </View>
        )}

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            opacity: 0,
          }}>
          <input
            ref={inputWebRef}
            type={getInputType()}
            value={
              selectedDate
                ? dayjs(selectedDate).format(getInputValueFormat())
                : ''
            }
            onChange={handleWebChange}
            onBlur={() => setShow(false)}
            autoFocus
            min={
              minDate ? dayjs(minDate).format(getInputValueFormat()) : undefined
            }
            max={
              maxDate ? dayjs(maxDate).format(getInputValueFormat()) : undefined
            }
            aria-label={label || 'Date picker'}
          />
        </div>
      </TouchableOpacity>
    );
  }

  // On native, use the native DatePicker component if available
  return (
    <TouchableOpacity
      activeOpacity={0.7}
      disabled={disabled}
      style={[
        style,
        disabled && {
          opacity: 0.5,
        },
      ]}
      onPress={showDatePicker}>
      {customComponent ? (
        customComponent()
      ) : (
        <View style={{width: '100%'}}>
          {CustomTextInput ? (
            <CustomTextInput
              label={label}
              value={getValue()}
              editable={false}
              onChangeText={onValueChange}
              showRightIcon
              rightIconName={'calendar'}
              isRequired={isRequired}
              style={{pointerEvents: 'none'}}
              {...props}
            />
          ) : (
            <StyledTextInput
              value={getValue()}
              editable={false}
              placeholder={label}
              style={[{pointerEvents: 'none'}, style]}
            />
          )}
        </View>
      )}

      {show && NativeDatePicker && (
        <NativeDatePicker
          modal
          mode={mode}
          open={show}
          locale="en"
          date={selectedDate || new Date()}
          maximumDate={maxDate ? dayjs(maxDate).toDate() : undefined}
          minimumDate={minDate ? dayjs(minDate).toDate() : undefined}
          onConfirm={onChange}
          onCancel={() => setShow(false)}
        />
      )}
    </TouchableOpacity>
  );
};

export default GenericDatePicker;
